import { useState, useEffect } from 'react';
import { User, Search } from 'lucide-react';

export interface UserOption {
  userId: string;
  fullName: string;
  email: string;
  role: string;
  avatar?: string;
}

interface UserAutocompleteProps {
  users: UserOption[];
  onSelect: (userId: string) => void;
  selectedUserId?: string;
  placeholder?: string;
  disabled?: boolean;
}

const UserAutocomplete = ({ users, onSelect, selectedUserId, placeholder = "Search users...", disabled = false }: UserAutocompleteProps) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<UserOption[]>([]);
  const [isOpen, setIsOpen] = useState(false);

  // Keep the input in sync when the selected user changes
  useEffect(() => {
    if (selectedUserId) {
      const selected = users.find(u => u.userId === selectedUserId);
      setQuery(selected ? `${selected.fullName}` : '');
    } else {
      setQuery('');
    }
  }, [selectedUserId, users]);

  useEffect(() => {
    if (query.trim().length === 0) {
      setResults([]);
      setIsOpen(false);
      return;
    }
    const lower = query.toLowerCase();
    const filteredUsers = users.filter(u => 
      u.fullName.toLowerCase().includes(lower) || 
      u.email.toLowerCase().includes(lower) ||
      u.role.toLowerCase().includes(lower)
    );
    setResults(filteredUsers.slice(0, 10)); // Limit to 10 results
    setIsOpen(filteredUsers.length > 0);
  }, [query, users]);

  const handleSelect = (user: UserOption) => {
    onSelect(user.userId);
    setQuery(`${user.fullName}`);
    setResults([]);
    setIsOpen(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
    // Clear selection if user is typing
    if (selectedUserId && e.target.value !== `${users.find(u => u.userId === selectedUserId)?.fullName} (${users.find(u => u.userId === selectedUserId)?.email})`) {
      onSelect('');
    }
  };

  return (
    <div className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
        <input
          type="text"
          value={query}
          onChange={handleInputChange}
          onFocus={() => query && setIsOpen(results.length > 0)}
          onBlur={() => setTimeout(() => setIsOpen(false), 200)} // Delay to allow click
          placeholder={placeholder}
          disabled={disabled}
          className="w-full pl-10 pr-4 py-2 bg-white/50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors disabled:bg-gray-100 disabled:text-gray-500"
        />
      </div>
      
      {isOpen && results.length > 0 && (
        <div className="absolute z-50 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
          {results.map(user => (
            <button
              key={user.userId}
              onClick={() => handleSelect(user)}
              className="w-full text-left px-4 py-3 hover:bg-indigo-50 transition-colors border-b border-gray-100 last:border-b-0"
            >
              <div className="flex items-center gap-3">
                {user.avatar ? (
                  <img
                    src={user.avatar}
                    alt={user.fullName}
                    className="w-8 h-8 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                    <User className="w-4 h-4 text-gray-500" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {user.fullName}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {user.email}
                  </p>
                </div>
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                  {user.role}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default UserAutocomplete;
